/**
 * Day-pill selector shared by print dialogs — one circular pill per
 * candidate day, toggling membership in the print selection. Extracted from
 * the byte-identical markup in the serve print dialog and the chefs print
 * dialog so the pill affordance (selected fill, disabled strikethrough,
 * aria-pressed/-disabled) can't drift between surfaces.
 *
 * Pills are always `type="button"` — every dialog that hosts them is (or is
 * becoming) a native <form>, and a bare button inside a form is an
 * accidental submit.
 */

import { cn } from '@ordo/ui/cn'

export interface DayPillOption {
  /** `YYYY-MM-DD` identity, passed back on toggle. */
  iso: string
  /** One-or-two-character pill face, e.g. "M", "Tu". */
  pillLabel: string
  /** Accessible name, e.g. "Mon 08/10". */
  shortDateLabel: string
  /** False ⇒ the day has nothing to print; renders struck-through + disabled. */
  hasService: boolean | null
  selected: boolean
}

export function DayPills({ days, onToggle }: { days: readonly DayPillOption[]; onToggle: (iso: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      {days.map((day) => {
        const disabledPill = day.hasService === false
        const selected = !disabledPill && day.selected
        return (
          <button
            key={day.iso}
            type="button"
            onClick={disabledPill ? undefined : () => onToggle(day.iso)}
            aria-pressed={selected}
            aria-disabled={disabledPill}
            aria-label={day.shortDateLabel}
            disabled={disabledPill}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-full border text-xs font-medium transition-colors',
              disabledPill
                ? 'cursor-not-allowed border-border bg-muted text-muted-foreground/60 line-through'
                : selected
                  ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'border-border bg-background text-foreground hover:bg-muted',
            )}>
            {day.pillLabel}
          </button>
        )
      })}
    </div>
  )
}
